import type { VehicleInterestDetails } from '@/services/conversations.api'
import { Badge, Paper, Stack, Text, Tooltip } from '@mantine/core'

interface VehicleInterestBadgeProps {
  /** Short label (e.g. "2025 Ram 2500 (Stock #1T252785)") */
  label: string
  /** Optional enriched details for tooltip; when present, hover shows a details popover */
  details?: VehicleInterestDetails | null
  /** Render as badge (default) or inline text */
  variant?: 'badge' | 'text'
  /** For variant="text", pass through text size/color props */
  size?: 'xs' | 'sm' | 'md'
  c?: string
  truncate?: boolean
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <Text size="xs" c="dark.6">
      <Text span fw={600} c="dark.7" inherit>
        {label}:
      </Text>{' '}
      {String(value)}
    </Text>
  )
}

export function VehicleInterestBadge({ label, details, variant = 'badge', size = 'md', c, truncate }: VehicleInterestBadgeProps) {
  const hasDetails = details && Object.keys(details).length > 0

  const tooltipContent = hasDetails ? (
    <Paper p="sm" withBorder shadow="sm" bg="gray.0" style={{ maxWidth: 340, maxHeight: 400, overflow: 'auto' }}>
      <Stack gap={4}>
        {(details!.year != null || details!.make || details!.model) && (
          <Text size="sm" fw={600} c="dark.7">
            {[details!.year, details!.make, details!.model].filter(Boolean).join(' ')}
          </Text>
        )}
        {details!.trim && <DetailRow label="Trim" value={details!.trim} />}
        {details!.style && <DetailRow label="Style" value={details!.style} />}
        {details!.stockNumber && <DetailRow label="Stock" value={details!.stockNumber} />}
        {details!.vin && <DetailRow label="VIN" value={details!.vin} />}
        {details!.mileage != null && <DetailRow label="Mileage" value={`${details!.mileage.toLocaleString()} mi`} />}
        {details!.exteriorColor && <DetailRow label="Exterior" value={details!.exteriorColor} />}
        {details!.interiorColor && <DetailRow label="Interior" value={details!.interiorColor} />}
        {details!.listedPrice != null && <DetailRow label="Price" value={`$${details!.listedPrice.toLocaleString()}`} />}
        {details!.vehicleType && <DetailRow label="Type" value={details!.vehicleType} />}
        {details!.vehicleClass && <DetailRow label="Class" value={details!.vehicleClass} />}
        {details!.transmission && <DetailRow label="Transmission" value={details!.transmission} />}
        {details!.drivetrain && <DetailRow label="Drivetrain" value={details!.drivetrain} />}
        {details!.engine && <DetailRow label="Engine" value={details!.engine} />}
        {details!.engineType && <DetailRow label="Engine type" value={details!.engineType} />}
        {details!.bodyType && <DetailRow label="Body" value={details!.bodyType} />}
        {details!.fuelType && <DetailRow label="Fuel" value={details!.fuelType} />}
        {details!.equipment && (
          <div>
            <Text size="xs" fw={600} c="dark.7">
              Equipment:
            </Text>
            <Text size="xs" c="dark.6" lineClamp={4} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {details!.equipment}
            </Text>
          </div>
        )}
        {details!.features && details!.features.length > 0 && (
          <div>
            <Text size="xs" fw={600} c="dark.7">
              Features:
            </Text>
            <Text size="xs" c="dark.6">
              {details!.features.join(', ')}
            </Text>
          </div>
        )}
      </Stack>
    </Paper>
  ) : null

  const content =
    variant === 'badge' ? (
      <Badge size={size} variant="light" color="green">
        {label}
      </Badge>
    ) : (
      <Text size={size} c={c} truncate={truncate}>
        {label}
      </Text>
    )

  if (tooltipContent) {
    return (
      <Tooltip
        label={tooltipContent}
        withArrow
        position="top"
        multiline
        styles={{
          tooltip: {
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
          },
        }}
      >
        <span style={{ display: 'inline-block' }}>{content}</span>
      </Tooltip>
    )
  }

  return <>{content}</>
}
